import React, { useState, useEffect, useCallback } from "react";
import * as S from "./styles";

import { usePolicemanRegister } from "contexts/register/PolicemanContext";

import Input from "../../../Input";
import MaskedInput from "../../../MaskedInput";
import { IUser } from "interfaces/User";

import { ToggleButton } from "../../../ToggleButton";
import { IIbgeStates, getUfs } from "services/UfServices";
import { AutoComplete } from "../../../Autocomplete";
import { IProvider } from "interfaces/Provider";
import { IPoliceman } from "interfaces/Policeman";
import { isEmailValid } from "utils/isEmailValid";
import { IContact } from "interfaces/Contact";
import { getUserService } from "services/UserServices";
import { useDataList } from "hooks/useDataList";

type Props = {
  isEditting: boolean;
  policemanData?: IPoliceman;
  contactData?: IContact;
};

function PolicemanForm({ isEditting, policemanData }: Props) {
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [agentToggle, setAgentToggle] = useState<boolean>(false);
  const [workingToggle, setWorkingToggle] = useState<boolean>(false);
  const [email, setEmail] = useState<string>("");

  const [cpf, setCpf] = useState<string>("");
  const [rg, setRg] = useState<string>("");
  const [phone, setPhone] = useState<string>("");
  const [name, setName] = useState<string>("");

  const {
    policeman,
    setPoliceman,
    contact,
    setContact,
    errors,
    searching,
    addError,
    removeError,
    setSelectedProvider,
    selectedProvider,
    isClientAssociationRegister,
  } = usePolicemanRegister();

  const [ufs, setUfs] = useState<IIbgeStates[]>([]);

  useEffect(() => {
    getUfs().then((ufList) => {
      setUfs(ufList);
    });
  }, []);

  const providerData = useDataList<IProvider>({
    queryKey: 'listAllProviders',
    route: 'providers',
    filter: {
      notPage: true,
    }
  });

  const checkIfUserExist = useCallback(async () => {
    try {
      const userExist = await getUserService({ where: { email } });

      if (userExist.status) {
        addError("emailExists", "E-mail já registrado");
        return;
      }
    } catch {
      removeError("emailExists");
      return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]);

  useEffect(() => {
    if (isEmailValid(email)) {
      checkIfUserExist();
    }
  }, [email, checkIfUserExist]);

  useEffect(() => {
    setAgentToggle(policeman.publicAgent);
    setWorkingToggle(policeman.working);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function changeUserValue(value: string, key: keyof IUser) {
    setPoliceman((_value) => {
      if (_value.user) _value.user[key] = value as never;
      return _value;
    });
  }

  function getPolicemanUserValue(key: keyof IUser): string {
    return (policemanData?.user[key] as string) || "";
  }

  function validateCPF(event: React.ChangeEvent<HTMLInputElement>) {
    const cpf = event.target.value.replace("_", "");
    if (cpf.length < 14) {
      addError("cpf", "CPF inválido");
      return;
    }

    removeError("cpf");
  }

  function validateRG(event: React.ChangeEvent<HTMLInputElement>) {
    const rg = event.target.value.replace("_", "");
    if (rg.length < 12) {
      addError("rg", "RG inválido");
      return;
    }

    removeError("rg");
  }

  function validateEmail(event: React.ChangeEvent<HTMLInputElement>) {
    const email = event.target.value;

    if (!isEmailValid(email)) {
      addError("email", "E-mail inválido");
      return;
    }

    removeError("email");
  }

  function validatePhone(event: React.ChangeEvent<HTMLInputElement>) {
    const phoneNumber = event.target.value
      .replaceAll("_", "")
      .replaceAll(" ", "");
    if (phoneNumber.length < 13) {
      addError("phoneNumber", "Número de telefone inválido");
      return;
    }

    removeError("phoneNumber");
  }

  function validateCNPJ(event: React.ChangeEvent<HTMLInputElement>) {
    const cnpj = event.target.value.replace("_", "");
    if (cnpj.length > 1 && cnpj.length < 18) {
      addError("cnpj", "CNPJ inválido");
      return;
    }

    removeError("cnpj");
  }

  if (searching) {
    return (
      <S.LoadingWrapper>
        <span>Buscando dados do pronta-resposta...</span>
      </S.LoadingWrapper>
    );
  }

  return (
    <S.Wrapper>
      <S.Form>
        <S.FormBody>
          <Input
            defaultValue={policeman.user?.name ?? ""}
            id="name"
            name="name"
            onChange={(event) => {
              setPoliceman((_value) => {
                if (_value.user) _value.user.name = event.target.value;
                return _value;
              });
              setName(event.target.value);
            }}
            label="Nome completo"
            required={true}
            type="text"
            error={errors.name || errors["user.name"]}
            apiErrorName="user.name"
            handleApiErrorRemove={() => removeError("user.name")}
          />
          <Input
            id="email"
            name="email"
            onBlur={validateEmail}
            defaultValue={policeman.user?.email ?? ""}
            onChange={(event) => {
              setPoliceman((_value) => {
                if (_value.user) _value.user.email = event.target.value;
                return _value;
              });
              setEmail(event.target.value);
            }}
            label="E-mail"
            required={true}
            type="email"
            error={
              errors.email ||
              errors["users.email"] ||
              errors["user.email"] ||
              errors.emailExists ||
              errors["client.emailExists"] ||
              errors["clients.emailExists"]
            }
            apiErrorName="user.email"
            handleApiErrorRemove={() => removeError("user.email")}
          />
          <S.DocumentContainer>
            <MaskedInput
              value={policeman?.user?.cpf ?? ""}
              id="CPF"
              name="CPF"
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setPoliceman((_value) => {
                  if (_value.user)
                    _value.user = {
                      ..._value.user,
                      cpf: event.target.value,
                    };
                  return _value;
                });
                setCpf(event.target.value);
              }}
              onBlur={validateCPF}
              label="CPF"
              required={true}
              type="text"
              mask="999.999.999-99"
              error={errors.cpf || errors["user.cpf"]}
              duplicatedError={errors["users.cpf"]}
              apiErrorName="user.cpf"
              handleApiErrorRemove={() => removeError("user.cpf")}
            />
            <MaskedInput
              id="RG"
              value={policeman?.user?.rg ?? ""}
              name="RG"
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setPoliceman((_value) => {
                  if (_value.user) _value.user.rg = event.target.value;
                  return _value;
                });
                setRg(event.target.value);
              }}
              label="RG"
              onBlur={validateRG}
              required={true}
              type="text"
              mask="99.999.999-*"
              error={errors.rg || errors["user.rg"]}
              duplicatedError={errors["users.rg"]}
              apiErrorName="user.rg"
              handleApiErrorRemove={() => removeError("user.rg")}
            />
          </S.DocumentContainer>
          <MaskedInput
            id="cellphone"
            name="cellphone"
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setContact((value) => {
                value.phoneNumber = event.target.value;
                return value;
              });
              setPhone(event.target.value);
            }}
            value={contact?.phoneNumber}
            label="Contato telefônico"
            required={true}
            type="text"
            onBlur={validatePhone}
            mask="(99) 9 9999-9999"
            error={errors.phoneNumber || errors["contact.phoneNumber"]}
          />

          <MaskedInput
            id="cnpj"
            name="cnpj"
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setPoliceman((value) => {
                if (value.user)
                  value.user = {
                    ...value.user,
                    cnpj: event.target.value,
                  };
                return value;
              })
            }
            defaultValue={policeman?.user?.cnpj ?? ""}
            label="CNPJ"
            required={true}
            onBlur={validateCNPJ}
            type="text"
            mask="99.999.999/9999-99"
            error={errors.cnpj || errors["policeman.cnpj"]}
            duplicatedError={errors["policemen.cnpj"]}
          />
          <AutoComplete
            id="region"
            label="Região"
            options={ufs}
            getOptionLabel={(option) => option.nome}
            defaultValue={{
              id: Math.random(),
              nome: policemanData?.region ?? '',
              sigla: ''
            } as IIbgeStates}
            placeholder="Lista de regiões registradas"
            onChange={(selected) => {
              if (selected) {
                setPoliceman((_value) => {
                  _value.region = (selected as IIbgeStates).nome as string;
                  return _value;
                });
              }
            }}
            error={errors.region || errors["policeman.region"]}
            apiErrorName="policeman.region"
            handleApiErrorRemove={() => removeError("policeman.region")}
          />
          <S.ButtonContainer>
            <ToggleButton
              label="Agente público"
              response={!agentToggle}
              onClick={(isTrue) => {
                setAgentToggle(isTrue);
                setPoliceman((value) => {
                  value.publicAgent = isTrue;
                  return value;
                });
              }}
              leftClick={() => { }}
              rightClick={() => { }}
            />
            <ToggleButton
              response={!workingToggle}
              label="Na ativa"
              onClick={(isTrue) => {
                setWorkingToggle(isTrue);
                setPoliceman((value) => {
                  value.working = isTrue;
                  return value;
                });
              }}
              leftClick={() => { }}
              rightClick={() => { }}
            />
          </S.ButtonContainer>

          {!isEditting && !isClientAssociationRegister && (
            <>
              <Input
                id="password"
                name="password"
                defaultValue={
                  policeman?.user?.password
                    ? policeman.user.password
                    : getPolicemanUserValue("password")
                }
                onChange={(event) =>
                  changeUserValue(event.target.value, "password")
                }
                label="Senha"
                required={true}
                type={showPassword ? "text" : "password"}
                apiErrorName="user.password"
                handleApiErrorRemove={() => removeError("user.password")}
                error={errors.password || errors["user.password"]}
              />
              {/* <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className={styles.viewPasswordButton}
                >
                  <img src={EyeImg} alt="Ícone de olho" />
                </button> */}
            </>
          )}

          {!providerData.isLoading && providerData.data && (
            <AutoComplete
              id="supplier"
              label="Selecione um fornecedor - (Opcional)"
              isSearchable
              options={providerData.data.items || []}
              getOptionLabel={(option) => option.name}
              placeholder="Selecione um fornecedor"
              defaultValue={selectedProvider}
              onChange={(selected) => {
                setSelectedProvider(selected as IProvider);
              }}
              error={
                (errors.servicesIds && "Nenhum fornecedor foi selecionado") ||
                errors["providerId"]
              }
            />
          )}
        </S.FormBody>
      </S.Form>
    </S.Wrapper>
  );
}

export { PolicemanForm };
